// Helpers for the bucket views to read both the new v1 payload shape
// (`payload.default_fields` / `payload.structured_fields` /
// `payload.blurb_template_id`) and the legacy flat payload during the
// cutover. Until every row is re-emitted by N8N v1, the dispatcher will see
// a mix of the two shapes; readers fall back to legacy keys when the v1
// keys are missing.

import type { ApprovalAction, ApprovalRequest } from '@/lib/types';
import { renderBlurb } from '../blurbTemplates';
import type { DefaultFields, ExpenseRebateLine } from '../types';

// Extra fields the AP detail page receives but the base ApprovalRequest type
// does not declare. Kept narrow on purpose — read-only.
type RequestWithActions = ApprovalRequest & { actions?: ApprovalAction[] };

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function readLineArray(value: unknown): ExpenseRebateLine[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((line) => asRecord(line))
    .filter((line) => Object.keys(line).length > 0) as ExpenseRebateLine[];
}

function selectedSupplierName(value: unknown): string | null {
  const candidates = Array.isArray(value) ? value.map(asRecord) : [];
  for (const candidate of candidates) {
    const name =
      readString(candidate['company_name']) ??
      readString(candidate['supplier_name']) ??
      readString(candidate['short_desc']) ??
      readString(candidate['vendor']);
    if (name) return name;
  }
  return null;
}

function readWholeNumberString(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (Number.isFinite(n)) return String(Math.ceil(n));
  return readString(value);
}

function readServicePostedAmounts(payload: Record<string, unknown>): {
  subtotal_before_tax: string | null;
  tax_amount: string | null;
  gross_amount: string | null;
} | null {
  const structured = asRecord(payload['structured_fields']);
  const preflight = asRecord(structured['service_preflight']);
  if (Object.keys(preflight).length === 0) return null;

  const serviceLines = Array.isArray(preflight['service_lines'])
    ? preflight['service_lines']
        .map((line) => asRecord(line))
        .filter((line) => Object.keys(line).length > 0)
    : [];

  if (serviceLines.length > 0) {
    const subtotal = serviceLines.reduce((sum, line) => {
      const amount = readWholeNumberString(line['amount']);
      if (amount !== null) return sum + Number(amount);
      const taxable = Number(readWholeNumberString(line['taxable_amount']) ?? 0);
      const nonTaxable = Number(readWholeNumberString(line['non_taxable_amount']) ?? 0);
      return sum + taxable + nonTaxable;
    }, 0);
    const taxAmount = serviceLines.reduce((sum, line) => {
      if (line['stax_checked'] !== true) return sum;
      return sum + Number(readWholeNumberString(line['tax_amount']) ?? 0);
    }, 0);
    return {
      subtotal_before_tax: String(subtotal),
      tax_amount: String(taxAmount),
      gross_amount: String(subtotal + taxAmount),
    };
  }

  const subtotal =
    Number(readWholeNumberString(preflight['taxable_amount']) ?? 0) +
    Number(readWholeNumberString(preflight['non_taxable_amount']) ?? 0);
  const taxAmount = Number(readWholeNumberString(preflight['tax_amount']) ?? 0);
  const gross =
    readWholeNumberString(preflight['gross_amount']) ?? String(subtotal + taxAmount);

  return {
    subtotal_before_tax: subtotal ? String(subtotal) : null,
    tax_amount: taxAmount ? String(taxAmount) : "0",
    gross_amount: gross,
  };
}

function legacyDriveFileId(payload: Record<string, unknown>): string | null {
  const direct = readString(payload['drive_file_id']);
  if (direct !== null) return direct;
  const attachments = payload['attachments'];
  if (Array.isArray(attachments)) {
    for (const att of attachments) {
      const rec = asRecord(att);
      const type = readString(rec['type']);
      if (type === 'drive') {
        const id = readString(rec['id']) ?? readString(rec['file_id']);
        if (id) return id;
      }
    }
  }
  return null;
}

function legacyGrnNo(
  payload: Record<string, unknown>,
  oracleResult: Record<string, unknown>,
): string | null {
  const direct = readString(payload['grn_no']);
  if (direct !== null) return direct;
  const fromOracle = readString(oracleResult['grn_no']);
  if (fromOracle !== null) return fromOracle;
  const grnNos = oracleResult['grn_nos'];
  if (Array.isArray(grnNos)) {
    const joined = grnNos.map((v) => readString(v)).filter(Boolean).join(',');
    if (joined) return joined;
  }
  return null;
}

export function readDefaultFields(req: ApprovalRequest): DefaultFields {
  const payload = asRecord(req.payload);
  const v1 = asRecord(payload['default_fields']);
  const summary = asRecord(req.summary);
  const gemini = {
    ...asRecord(v1['gemini_raw']),
    ...asRecord(payload['gemini_raw']),
  };
  const oracleResult = asRecord(payload['oracle_result']);
  const structured = asRecord(payload['structured_fields']);
  const refresh = asRecord(structured['grn_candidate_refresh']);
  const extractedPoFallback =
    (req.bucket === 'GRN_NOT_FOUND' || req.bucket === 'GRN_UNAPPROVED') &&
    refresh['fallback'] === 'extracted_po_no_rows_supplier_date';
  const servicePostedAmounts =
    req.bucket === 'POSTED_SERVICE' ? readServicePostedAmounts(payload) : null;
  // Slice 4 Task 4.4: gemini_raw is a final-fallback only for DUPLICATE rows
  // (and that bucket's view applies it explicitly per-field). Other buckets
  // surface either v1 default_fields or top-level/legacy payload keys; if
  // those are absent we render '—' rather than masking a missing-field bug
  // by silently scraping the raw OCR JSON.
  const allowGeminiFallback = req.bucket === 'DUPLICATE';

  const pick = (...keys: string[]): string | null => {
    for (const key of keys) {
      const candidate =
        readString(v1[key]) ?? readString(payload[key]) ?? readString(summary[key]);
      if (candidate !== null) return candidate;
    }
    if (allowGeminiFallback) {
      for (const key of keys) {
        const candidate = readString(gemini[key]);
        if (candidate !== null) return candidate;
      }
    }
    return null;
  };

  // Post-bucket-contract fix: legacy rows have field data at flat
  // `payload.*` paths (and grn/drive IDs sometimes only inside
  // `oracle_result`/`attachments`). Synthesize from those when v1
  // default_fields is missing the field — never overwrite a non-empty v1
  // value.
  const grnNo =
    readString(v1['grn_no']) ?? legacyGrnNo(payload, oracleResult) ?? pick('grn_no');
  const driveFileId = readString(v1['drive_file_id']) ?? legacyDriveFileId(payload);
  const fbrNumber =
    pick('fbr_number', 'fbr_ref') ?? readString(gemini['fbr_number']);
  const ocrVendor =
    readString(v1['vendor']) ??
    readString(payload['vendor']) ??
    readString(summary['vendor']) ??
    (allowGeminiFallback ? readString(gemini['vendor']) : null);
  const selectedSupplier =
    selectedSupplierName(structured['supplier_candidates_selected']) ??
    selectedSupplierName(payload['supplier_candidates_selected']);
  const supplierIdentity =
    readString(payload['supplier_id']) ??
    readString(v1['supplier_id']) ??
    readString(structured['supplier_id']) ??
    readString(payload['supplier_code']) ??
    readString(v1['supplier_code']) ??
    readString(structured['supplier_code']) ??
    selectedSupplier;
  const resolvedVendor =
    supplierIdentity === null
      ? null
      : selectedSupplier ??
        readString(payload['resolved_supplier_name']) ??
        readString(structured['resolved_supplier_name']) ??
        readString(payload['supplier_name']) ??
        readString(structured['supplier_name']) ??
        readString(payload['supplier']);

  return {
    vendor: resolvedVendor ?? pick('vendor', 'supplier', 'supplier_name'),
    ocr_vendor: ocrVendor,
    invoice_no: pick('invoice_no'),
    invoice_date: pick('invoice_date', 'date'),
    fbr_number: fbrNumber,
    ntn: pick('ntn', 'supplier_ntn'),
    po_no: extractedPoFallback ? null : pick('po_no', 'po_number'),
    grn_no: grnNo,
    subtotal_before_tax:
      servicePostedAmounts?.subtotal_before_tax ?? pick('subtotal_before_tax', 'subtotal'),
    tax_amount: servicePostedAmounts?.tax_amount ?? pick('tax_amount'),
    gross_amount: servicePostedAmounts?.gross_amount ?? pick('gross_amount', 'amount'),
    drive_file_id: driveFileId,
    gemini_raw: Object.keys(gemini).length > 0 ? gemini : null,
  };
}

function legacyOracleResult(payload: Record<string, unknown>): Record<string, unknown> {
  const legacy = asRecord(payload['oracle_result']);
  // Map legacy oracle_result keys (oracle_pi_id, oracle_purch_invoice_ref_no,
  // oracle_pi_book_id, oracle_gl_date, oracle_due_date, oracle_post_verified)
  // and the older flat-payload variants onto the new shape
  // (purchase_invoice_id, ref_no, pi_book_id, gl_date, due_date,
  // post_verified_at). `oracle_post_verified` is a boolean — leave
  // post_verified_at null when only the boolean is present (keeps the UI
  // honest; we never had the actual timestamp).
  const piId =
    readString(legacy['purchase_invoice_id']) ??
    readString(legacy['oracle_pi_id']) ??
    readString(legacy['oracle_purchase_invoice_id']) ??
    readString(payload['oracle_purch_invoice_id']) ??
    readString(payload['oracle_purchase_invoice_id']);
  const refNo =
    readString(legacy['ref_no']) ??
    readString(legacy['oracle_purch_invoice_ref_no']) ??
    readString(payload['oracle_purch_invoice_ref_no']);
  const bookId =
    readString(legacy['pi_book_id']) ?? readString(legacy['oracle_pi_book_id']);
  const glDate =
    readString(legacy['gl_date']) ?? readString(legacy['oracle_gl_date']);
  const dueDate =
    readString(legacy['due_date']) ?? readString(legacy['oracle_due_date']);
  const postVerifiedAt =
    readString(legacy['post_verified_at']) ??
    readString(legacy['oracle_post_verified_at']);

  const result: Record<string, unknown> = {};
  if (piId) result['purchase_invoice_id'] = piId;
  if (refNo) result['ref_no'] = refNo;
  if (bookId) result['pi_book_id'] = bookId;
  if (glDate) result['gl_date'] = glDate;
  if (dueDate) result['due_date'] = dueDate;
  if (postVerifiedAt) result['post_verified_at'] = postVerifiedAt;
  return result;
}

function mergeOracleResultFallbacks(
  current: Record<string, unknown>,
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const fallback = legacyOracleResult(payload);
  const merged = { ...fallback, ...current };
  return Object.fromEntries(
    Object.entries(merged).filter(([, value]) => value !== null && value !== undefined && value !== ''),
  );
}

function legacyDuplicateOf(payload: Record<string, unknown>): {
  duplicateOf: Record<string, unknown>;
  duplicateReason: string | null;
} {
  const refNo =
    readString(payload['oracle_duplicate_ref_no']) ??
    readString(payload['oracle_purch_invoice_ref_no']);
  const piId =
    readString(payload['oracle_duplicate_purch_invoice_id']) ??
    readString(payload['oracle_purch_invoice_id']) ??
    readString(payload['oracle_purchase_invoice_id']);
  const postedDate =
    readString(payload['oracle_duplicate_posted_date']) ??
    readString(payload['oracle_invoice_date']);

  const duplicateOf: Record<string, unknown> = {};
  if (refNo) duplicateOf['ref_no'] = refNo;
  if (piId) duplicateOf['purchase_invoice_id'] = piId;
  if (postedDate) duplicateOf['posted_date'] = postedDate;

  const duplicateReason =
    readString(payload['duplicate_reason']) ??
    readString(asRecord(payload['oracle_result'])['duplicate_reason']);

  return { duplicateOf, duplicateReason };
}

export function readStructuredFields(req: ApprovalRequest): Record<string, unknown> {
  const payload = asRecord(req.payload);
  const v1 = asRecord(payload['structured_fields']);
  if (Object.keys(v1).length > 0) {
    const v1OracleResult = asRecord(v1['oracle_result']);
    const postedExpenseLines = readLineArray(
      v1OracleResult['expense_rebate_lines'] ??
        v1OracleResult['expense_rebate_readback'] ??
        payload['oracle_expense_rebate_lines'],
    );
    if (req.status === 'posted' && !asRecord(v1['oracle_result'])['purchase_invoice_id']) {
      const oracleResult = mergeOracleResultFallbacks(v1OracleResult, payload);
      if (Object.keys(oracleResult).length > 0) {
        return {
          ...v1,
          ...(postedExpenseLines.length > 0
            ? { expense_rebate_lines: postedExpenseLines }
            : {}),
          oracle_result: oracleResult,
        };
      }
    }
    if (Object.keys(v1OracleResult).length > 0) {
      const oracleResult =
        req.status === 'posted'
          ? mergeOracleResultFallbacks(v1OracleResult, payload)
          : v1OracleResult;
      return {
        ...v1,
        ...(postedExpenseLines.length > 0
          ? { expense_rebate_lines: postedExpenseLines }
          : {}),
        oracle_result: oracleResult,
      };
    }
    const v1ExpenseLines = readLineArray(v1['expense_rebate_lines']);
    return {
      ...v1,
      ...(v1ExpenseLines.length > 0 ? { expense_rebate_lines: v1ExpenseLines } : {}),
    };
  }

  // Legacy fall-through: synthesize the minimum structured_fields shape per
  // bucket so the bucket views render real data for pre-contract rows. Never
  // touch payload itself; callers used to receive the raw payload as a
  // last-resort, so keep that key set available too.
  if (req.status === 'posted') {
    const oracleResult = legacyOracleResult(payload);
    if (Object.keys(oracleResult).length > 0) {
      return { oracle_result: oracleResult };
    }
  }

  switch (req.bucket) {
    case 'POSTED':
    case 'POSTED_SERVICE': {
      const oracleResult = legacyOracleResult(payload);
      const expenseLines = readLineArray(
        asRecord(payload['oracle_result'])['expense_rebate_lines'] ??
          asRecord(payload['oracle_result'])['expense_rebate_readback'] ??
          payload['oracle_expense_rebate_lines'] ??
          payload['expense_rebate_lines'],
      );
      const out =
        Object.keys(oracleResult).length > 0 ? { oracle_result: oracleResult } : payload;
      return expenseLines.length > 0
        ? { ...out, expense_rebate_lines: expenseLines }
        : out;
    }
    case 'DUPLICATE': {
      const { duplicateOf, duplicateReason } = legacyDuplicateOf(payload);
      const out: Record<string, unknown> = {};
      if (Object.keys(duplicateOf).length > 0) out['duplicate_of'] = duplicateOf;
      if (duplicateReason) out['duplicate_reason'] = duplicateReason;
      return Object.keys(out).length > 0 ? out : payload;
    }
    default:
      // Other error buckets surface their data through top-level payload
      // keys. The view that needs a specific field reads it off the raw
      // payload via this same function.
      return payload;
  }
}

export function readExpenseRebateLines(req: ApprovalRequest): ExpenseRebateLine[] {
  const structured = readStructuredFields(req);
  const payload = asRecord(req.payload);
  const oracleResult = asRecord(structured['oracle_result']);
  return readLineArray(
    structured['expense_rebate_lines'] ??
      oracleResult['expense_rebate_lines'] ??
      oracleResult['expense_rebate_readback'] ??
      payload['expense_rebate_lines'],
  );
}

// Pick the actor email of the most recent terminal action (post / reject /
// post-failed-then-rescued), so PostedView/DuplicateView/PostedServiceView
// can show "Processed by you" instead of a UUID. Falls back to the row's
// resolved_by (UUID) and finally 'N8N' when nothing is on the request.
export function readProcessedBy(req: ApprovalRequest): string {
  const payload = asRecord(req.payload);
  const withActions = req as RequestWithActions;
  const actions = Array.isArray(withActions.actions) ? withActions.actions : [];
  // Prefer terminal actions; otherwise the most recent action is fine.
  const terminal = [...actions].reverse().find((a) =>
    [
      'post',
      'posted',
      'reject',
      'rejected',
      'post-service',
      'post-service-final',
      'service-posted',
    ].includes(a.action),
  );
  const pick = terminal ?? actions[actions.length - 1] ?? null;
  if (pick?.actor_email) return pick.actor_email;
  const postedBy = readString(payload['posted_by']) ?? readString(payload['processed_by']);
  if (postedBy) return postedBy;
  if (req.resolved_by) return req.resolved_by;
  if (req.status === 'posted') return 'N8N';
  return '—';
}

// resolved_at is the canonical "when did this leave the open queue" timestamp
// for both POSTED and DUPLICATE rows. Fall back to updated_at when an older
// row never had resolved_at written.
export function readResolvedAt(req: ApprovalRequest): string | null {
  return req.resolved_at ?? req.updated_at ?? null;
}

export function readBlurb(req: ApprovalRequest): string {
  const payload = asRecord(req.payload);
  const id = readString(payload['blurb_template_id']);
  if (!id) return '';
  const params = asRecord(payload['blurb_params']);
  return renderBlurb(id, params);
}
